import { Button } from '@ophelos/ui/components/button';
import { ScrollArea } from '@ophelos/ui/components/scroll-area';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@ophelos/ui/components/sidebar';
import {
  ArrowRightLeft,
  LayoutGrid,
  LifeBuoy,
  Milestone,
  PanelLeft,
  Presentation,
  Settings,
  ShieldHalf,
  TextCursorInput,
  Users,
} from 'lucide-react';
import { useWhatsAppMissingCountries } from '@/apps/admin/hooks/useWhatsAppRates';
import { useHubPathname } from '@/lib/global/hub-path';
import { useDemoModeAccess } from '@/lib/presentation-mode/access';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { filterItemsByRole } from '../../lib/app-navigation';
import { hasModuleAccess, hasModuleAdminAccess, hasModuleViewAccess } from '../../lib/module-permissions';
import { SidebarContextSwitcher } from '../context/SidebarContextSwitcher';
import { UserMenu } from '../user/UserMenu';
import { DemoModeSwitch } from './DemoModeSwitch';
import { HubNavLink } from './HubNavLink';
import { SidebarLogo } from './SidebarLogo';
import { type NavItem, NavItemCountBadge, SidebarNavGroup } from './SidebarNavGroup';

export function HubSidebar() {
  const { state, toggleSidebar } = useSidebar();
  const { roles, hasPermission, isSystemAdmin } = useUserPermissions();
  const pathname = useHubPathname();
  const { data: missingWhatsAppRates } = useWhatsAppMissingCountries(isSystemAdmin);
  const { allowed: canUseDemoMode } = useDemoModeAccess();
  const collapsed = state === 'collapsed';

  const navItems: NavItem[] = [{ name: 'Apps', to: '/apps', icon: LayoutGrid }];

  const configurationItems: NavItem[] = [
    { name: 'Settings', to: '/settings', icon: Settings, disabled: !hasModuleAdminAccess(hasPermission, 'config') },
    { name: 'Users', to: '/users', icon: Users, disabled: !hasModuleAdminAccess(hasPermission, 'users') },
  ];

  const previewItems: NavItem[] = [
    { name: 'Templates', to: '/app/library', icon: TextCursorInput, disabled: !hasModuleViewAccess(hasPermission, 'library') },
    { name: 'Customer Journey', to: '/playground/customer-journey', icon: Milestone, disabled: false },
  ];

  const footerItems: NavItem[] = [
    { name: 'Support', to: '/app/support', icon: LifeBuoy, disabled: false, visibility: ['settings-admin', 'operator-admin'] },
    { name: 'Config. transfers', to: '/app/import-export', icon: ArrowRightLeft, disabled: !hasModuleAccess(hasPermission, 'config') },
    {
      name: 'Admin Tools',
      to: '/app/admin',
      icon: ShieldHalf,
      disabled: false,
      visibility: ['system-admin'],
      badge: missingWhatsAppRates?.length,
    },
    {
      name: 'Demo mode',
      to: '/presentation',
      icon: Presentation,
      disabled: !canUseDemoMode,
      trailing: <DemoModeSwitch />,
    },
  ];

  const visibleFooterItems = filterItemsByRole(footerItems, roles).filter((item) => !item.disabled);

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader className="flex flex-row w-full h-13 gap-0 items-center py-0 justify-between">
        <SidebarLogo />
        {state === 'expanded' && (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={toggleSidebar}
            className="hover:bg-sidebar-accent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          >
            <PanelLeft className="size-4 text-muted-foreground" />
          </Button>
        )}
      </SidebarHeader>

      <div className={`my-2 ${state === 'collapsed' ? 'px-2' : ''}`}>
        <SidebarContextSwitcher collapsed={state === 'collapsed'} />
      </div>

      <SidebarContent className="pt-2">
        <ScrollArea className="h-full">
          <SidebarNavGroup items={filterItemsByRole(navItems, roles)} />
          <SidebarNavGroup items={filterItemsByRole(configurationItems, roles)} label="Configurations" className="mt-2" />
          <SidebarNavGroup items={filterItemsByRole(previewItems, roles)} label="Preview" className="mt-2" />
        </ScrollArea>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          {visibleFooterItems.map((item) => {
            const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`);
            return (
              <SidebarMenuItem key={item.to}>
                <SidebarMenuButton asChild isActive={isActive} tooltip={item.name}>
                  <HubNavLink to={item.to}>
                    <item.icon />
                    <span>{item.name}</span>
                    {!collapsed && <NavItemCountBadge count={item.badge} collapsed={false} />}
                  </HubNavLink>
                </SidebarMenuButton>
                {collapsed && <NavItemCountBadge count={item.badge} collapsed />}
                {!collapsed && item.trailing && (
                  <div data-sidebar="menu-action" className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">
                    {item.trailing}
                  </div>
                )}
              </SidebarMenuItem>
            );
          })}
          <SidebarMenuItem>
            <UserMenu />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail className="my-6" />
    </Sidebar>
  );
}
